/**
 * Is the agent actually working right now?
 *
 * THIS IS NOT `status === 'streaming'`, and that is the whole reason this file
 * exists. Anthropic flips the session status to "streaming" for BOTH a
 * tool-roundtrip pause AND "turn ended, ready for user input" — the two are
 * indistinguishable at the session level. Anything that treats `streaming` as
 * busy therefore latches ON when the agent has finished and stays there until
 * some later event moves the status, which reads to the user as a request that
 * never completes.
 *
 * The reliable signal is the explicit `pendingToolCalls` counter
 * (custom_tool_request − custom_tool_result) alongside the statuses that are
 * unambiguous on their own. The sidebar's typing dots have used that rule for a
 * while; the optimizer's action bar shipped with `streaming` in its predicate
 * and immediately produced a "Working…" state that outlived the work.
 *
 * One copy, so the next surface that needs it cannot re-derive the wrong one.
 */

/** Statuses that mean "working" with no further disambiguation needed. */
const WORKING_STATUSES = new Set(['running', 'initializing', 'rescheduling']);

/**
 * @param {object|null} session a reviewer/optimization session
 * @returns {boolean} true while the agent is genuinely producing or mid-tool-call
 */
export function isAgentWorking(session) {
  if (!session) return false;
  if (WORKING_STATUSES.has(session.status)) return true;
  return (session.pendingToolCalls || 0) > 0;
}

export default isAgentWorking;
